// Item Buttons for Services and Products
import { useState } from 'react';
import { Package, Coffee, Edit2 } from 'lucide-react';
import { iconMap } from './constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function ItemButton({ item, type, onAdd, isMasterAdmin, onPriceUpdate }) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newPrice, setNewPrice] = useState(item.price.toString());
  const [saving, setSaving] = useState(false);
  
  const IconComponent = iconMap[item.icon] || Package;

  const handleEditClick = (e) => {
    e.stopPropagation();
    setNewPrice(item.price.toString());
    setShowEditDialog(true);
  };

  const handleSavePrice = async () => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price <= 0) return;
    
    setSaving(true);
    try {
      await onPriceUpdate(item.id, type, price);
      setShowEditDialog(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={() => onAdd(item, type)}
        className={`pos-btn group relative ${item.has_branch_price ? 'ring-1 ring-blue-500/30' : ''}`}
        data-testid={`pos-item-${item.id}`}
      >
        {isMasterAdmin && (
          <button
            onClick={handleEditClick}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-zinc-700 hover:bg-gold/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            title="Editar precio de sucursal"
          >
            <Edit2 className="w-3 h-3 text-zinc-400 hover:text-gold" />
          </button>
        )}
        {item.has_branch_price && (
          <span className="absolute top-2 left-2 w-2 h-2 rounded-full bg-blue-500" title="Precio personalizado"></span>
        )}
        <div className="w-12 h-12 rounded-full bg-zinc-800 group-hover:bg-gold/20 flex items-center justify-center transition-colors">
          <IconComponent className="w-6 h-6 text-zinc-400 group-hover:text-gold transition-colors" />
        </div>
        <span className="text-sm text-zinc-300 font-medium">{item.name}</span>
        <div className="flex flex-col items-center">
          <span className={`font-semibold ${item.has_branch_price ? 'text-blue-400' : 'text-gold'}`}>
            ${item.price.toFixed(2)}
          </span>
          {item.has_branch_price && item.originalPrice && item.originalPrice !== item.price && (
            <span className="text-xs text-zinc-600 line-through">${item.originalPrice.toFixed(2)}</span>
          )}
        </div>
      </button>

      {/* Edit Price Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white">
              Editar Precio de Sucursal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-zinc-800 rounded-lg">
              <p className="text-white font-medium">{item.name}</p>
              <p className="text-xs text-zinc-500">
                {type === 'service' ? 'Servicio' : 'Producto'}
              </p>
              {item.originalPrice && (
                <p className="text-xs text-zinc-600 mt-1">
                  Precio base: ${item.originalPrice.toFixed(2)}
                </p>
              )}
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-400">
                💡 El precio que establezcas aplicará solo a esta sucursal
              </p>
            </div>
            <div>
              <label className="text-sm text-zinc-400 block mb-1">
                Precio para esta sucursal
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white pl-7 text-lg"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSavePrice()}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 border-zinc-700 text-zinc-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSavePrice}
                disabled={saving || parseFloat(newPrice) <= 0}
                className="flex-1 bg-gold text-black hover:bg-gold-dark"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SupplyButton({ supply, onAdd }) {
  return (
    <button
      onClick={() => onAdd(supply)}
      className="pos-btn group"
      data-testid={`pos-supply-${supply.id}`}
    >
      <div className="w-12 h-12 rounded-full bg-zinc-800 group-hover:bg-emerald-500/20 flex items-center justify-center transition-colors">
        <Coffee className="w-6 h-6 text-zinc-400 group-hover:text-emerald-400 transition-colors" />
      </div>
      <span className="text-sm text-zinc-300 font-medium">{supply.name}</span>
      <span className="text-emerald-400 text-xs">Stock: {Number(supply.stock).toFixed(1)} {supply.unit}</span>
    </button>
  );
}
